import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Typography,
  Tag,
  Tooltip,
  Spin,
  Upload,
  Progress
} from 'antd';
import { PlusOutlined, DeleteOutlined, CopyOutlined, UploadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { versionsApi } from '../api/versions';
import { appsApi } from '../api/apps';
import { Version, CreateVersionRequest, App, DistributionType } from '../types';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface VersionsProps {
  selectedAppId?: number;
}

const Versions: React.FC<VersionsProps> = ({ selectedAppId }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [versionDuplicateStatus, setVersionDuplicateStatus] = useState<{
    isChecking: boolean;
    isDuplicate: boolean | null;
    message: string;
  }>({
    isChecking: false,
    isDuplicate: null,
    message: '',
  });
  const [selectedApp, setSelectedApp] = useState<App | null>(null);

  const { data: versionsResponse, isLoading: versionsLoading } = useQuery({
    queryKey: ['versions', selectedAppId],
    queryFn: () => versionsApi.getVersions(1, 1000, selectedAppId),
    refetchOnMount: true,
    staleTime: 0, // Data luôn được coi là stale, sẽ refetch mỗi lần
  });

  // Debounce timer ref
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Function to check version duplicate with debounce
  const checkVersionDuplicate = useCallback(async (version: string) => {
    if (!version || version.trim() === '') {
      setVersionDuplicateStatus({
        isChecking: false,
        isDuplicate: null,
        message: '',
      });
      return;
    }

    setVersionDuplicateStatus(prev => ({ ...prev, isChecking: true }));

    try {
      const result = await versionsApi.checkVersionDuplicate(version);
      setVersionDuplicateStatus({
        isChecking: false,
        isDuplicate: result.is_duplicate,
        message: result.message,
      });
    } catch (error) {
      console.error('Error checking version duplicate:', error);
      setVersionDuplicateStatus({
        isChecking: false,
        isDuplicate: null,
        message: 'Lỗi khi kiểm tra version',
      });
    }
  }, []);

  // Debounced version check
  const debouncedCheckVersion = useCallback((version: string) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    debounceTimer.current = setTimeout(() => {
      checkVersionDuplicate(version);
    }, 500); // 500ms debounce
  }, [checkVersionDuplicate]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const { data: appsResponse } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.getApps(),
  });

  // Update selected app when app_id changes
  useEffect(() => {
    const appId = form.getFieldValue('app_id');
    if (appId && appsResponse) {
      const app = appsResponse.find((a: App) => a.id === appId);
      setSelectedApp(app || null);
    } else {
      setSelectedApp(null);
    }
  }, [form, appsResponse]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateVersionRequest) => {
      console.log('createMutation.mutationFn called with:', data);
      const result = await versionsApi.createVersion(data);
      console.log('createVersion API call result:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('Create version success:', data);
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      message.success('Tạo version thành công!');
      setIsModalVisible(false);
      setSelectedFile(null);
      setIsUploading(false);
      setUploadProgress(0);
      form.resetFields();
    },
    onError: (error: any) => {
      console.error('Create version error:', error);
      console.error('Error details:', error?.response?.data);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert('❌ Lỗi khi tạo version: ' + errorMessage);
      setIsUploading(false);
      setUploadProgress(0);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ appId, version }: { appId: number; version: string }) =>
      versionsApi.deleteVersion(appId, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      message.success('Xóa version thành công!');
    },
  });


  const handleDelete = (record: Version) => {
    deleteMutation.mutate({ appId: record.app_id, version: record.version });
  };


  const handleModalOk = async () => {
    try {
      console.log('handleModalOk - Starting process...');
      const values = await form.validateFields();
      console.log('Form values:', values);

      const isStoreType = selectedApp?.distribution_type === 'Store';

      if (!isStoreType && !selectedFile) {
        alert('❌ Vui lòng chọn file để upload!');
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);

      let fileExtension: string;
      let contentType: string;
      let fileSize: number;
      let filePath: string;

      if (isStoreType) {
        // For store type, use default values
        fileExtension = 'store';
        contentType = 'application/octet-stream';
        fileSize = 0;
        filePath = `store/${values.app_id}/${values.version}`;
      } else {
        // For binary type, get from selected file
        console.log('Selected file:', selectedFile!.name, 'Size:', selectedFile!.size);
        fileExtension = selectedFile!.name.split('.').pop() || '';
        contentType = selectedFile!.type || 'application/octet-stream';
        fileSize = selectedFile!.size;
      }

      console.log('File info - Extension:', fileExtension, 'Type:', contentType, 'Size:', fileSize);
      
      // Skip upload for store type, create version directly
      if (isStoreType) {
        console.log('Store type - skipping upload, creating version directly');

        // Get MD5 from form input or use default
        const md5 = values.file_md5 || '';

        // Create version record
        const createRequest: CreateVersionRequest = {
          app_id: values.app_id,
          version: values.version,
          file_extension: '', // Để trống file extension cho store type
          storage_path: undefined, // Không cần storage path cho store type
          file_md5: undefined, // Không cần MD5 cho store type
          file_release_notes: values.release_notes || '',
        };

        console.log('Creating version record for store:', createRequest);

        createMutation.mutate(createRequest);
        return;
      }

      // Use multipart upload for files > 10MB
      const MULTIPART_THRESHOLD = 10 * 1024 * 1024; // 10MB
      if (fileSize > MULTIPART_THRESHOLD) {
        // Multipart upload for large files
        console.log('Using multipart upload for file size:', fileSize);
        message.info('Đang upload file lớn với multipart upload (nhanh hơn)...');
        
        // Step 1: Initiate multipart upload
        console.log('Step 1: Initiating multipart upload...');
        const initiateResponse = await versionsApi.initiateMultipartUpload({
          app_id: values.app_id,
          version: values.version,
          file_extension: fileExtension,
          content_type: contentType,
          file_size: fileSize,
        });
        console.log('Initiate response:', initiateResponse);
        console.log('Total parts:', initiateResponse.part_urls.length);

        // Step 2: Upload parts in parallel
        const parts = initiateResponse.part_urls;
        const uploadedParts: Array<{ part_number: number; etag: string }> = [];
        const PART_SIZE = initiateResponse.part_size;
        
        console.log('Step 2: Uploading parts...');
        // Track completion status for each part
        let completedParts = 0;
        
        const uploadPart = async (partInfo: { part_number: number; upload_url: string }) => {
          const start = (partInfo.part_number - 1) * PART_SIZE;
          const end = Math.min(start + PART_SIZE, fileSize);
          const partData = selectedFile!.slice(start, end);
          
          console.log(`Uploading part ${partInfo.part_number}/${parts.length}...`);
          const etag = await versionsApi.uploadPartToS3(
            partInfo.upload_url,
            partData
          );
          console.log(`Part ${partInfo.part_number} uploaded. ETag:`, etag);
          
          // Update progress after each part completes
          completedParts++;
          const overallProgress = Math.round((completedParts / parts.length) * 100);
          setUploadProgress(Math.min(overallProgress, 99));
          console.log(`Progress: ${completedParts}/${parts.length} parts (${overallProgress}%)`);
          
          return { part_number: partInfo.part_number, etag };
        };

        // Upload 5 parts at a time
        const CONCURRENCY = 5;
        for (let i = 0; i < parts.length; i += CONCURRENCY) {
          const batch = parts.slice(i, i + CONCURRENCY);
          const batchResults = await Promise.all(batch.map(uploadPart));
          uploadedParts.push(...batchResults);
        }

        console.log('All parts uploaded. Uploaded parts:', uploadedParts);
        
        // Step 3: Complete multipart upload
        console.log('Step 3: Completing multipart upload...');
        await versionsApi.completeMultipartUpload({
          file_key: initiateResponse.file_key,
          upload_id: initiateResponse.upload_id,
          parts: uploadedParts,
        });
        console.log('Multipart upload completed successfully!');

        filePath = initiateResponse.file_key;
        setUploadProgress(100);
      } else {
        // Single-part upload for small files
        const uploadUrlResponse = await versionsApi.generateUploadUrl({
          app_id: values.app_id,
          version: values.version,
          file_extension: fileExtension,
          content_type: contentType,
        });

        await versionsApi.uploadFileToS3(
          uploadUrlResponse.upload_url,
          selectedFile!, // We know it's not null because we checked earlier
          contentType,
          setUploadProgress
        );

        filePath = uploadUrlResponse.file_path;
      }

      // Get MD5 from form input
      console.log('Getting MD5 from form...');
      const md5 = values.file_md5;
      console.log('MD5 from form:', md5);

      // Create version record
      const createRequest: CreateVersionRequest = {
        app_id: values.app_id,
        version: values.version,
        file_extension: fileExtension,
        storage_path: filePath,
        file_md5: md5,
        file_release_notes: values.release_notes || '',
      };

      console.log('Creating version record:', createRequest);
      console.log('Calling createMutation.mutate...');

      createMutation.mutate(createRequest);
      console.log('createMutation.mutate called successfully');

      // Don't reset states here - let onSuccess/onError handle it
    } catch (error) {
      console.error('Upload error:', error);
      alert('❌ Có lỗi xảy ra khi upload: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCreate = () => {
    console.log('handleCreate - Opening modal...');
    setIsModalVisible(true);
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSelectedFile(null);
    setUploadProgress(0);
    setVersionDuplicateStatus({
      isChecking: false,
      isDuplicate: null,
      message: '',
    });
    form.resetFields();
  };


  // Data is already filtered by backend
  const versions = versionsResponse?.data || [];

  const handleCopyMd5 = (md5: string) => {
    navigator.clipboard.writeText(md5);
    message.success('Đã copy MD5 vào clipboard!');
  };


  const columns = [
    {
      title: 'App',
      dataIndex: 'app_name',
      key: 'app_name',
    },
    {
      title: 'Distribution Type',
      key: 'distribution_type',
      render: (_: any, record: Version) => {
        const app = appsResponse?.find(app => app.id === record.app_id);
        const distributionType = app?.distribution_type;
        return (
          <Tag color={distributionType === 'Store' ? 'green' : 'blue'}>
            {distributionType === 'Store' ? 'Store' : 'Binary'}
          </Tag>
        );
      },
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'File Extension',
      dataIndex: 'file_extension',
      key: 'file_extension',
      render: (ext: string) => <Tag color="blue">{ext}</Tag>,
    },
    {
      title: 'Storage Path',
      dataIndex: 'storage_path',
      key: 'storage_path',
      render: (path: string) => (
        <div style={{ 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-all',
          maxWidth: '200px'
        }}>
          {path}
        </div>
      ),
    },
    {
      title: 'MD5',
      dataIndex: 'md5',
      key: 'md5',
      render: (md5: string) => (
        md5 ? (
          <Tooltip title="Click để copy">
            <span 
              style={{ 
                fontFamily: 'monospace', 
                fontSize: '11px',
                cursor: 'pointer',
                color: '#1890ff'
              }}
              onClick={() => handleCopyMd5(md5)}
            >
              {md5}
            </span>
          </Tooltip>
        ) : '-'
      ),
      width: 280,
    },
    {
      title: 'Release Notes',
      dataIndex: 'release_notes',
      key: 'release_notes',
      render: (notes: string) => (
        <div style={{ 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-word',
          maxWidth: '300px'
        }}>
          {notes || '-'}
        </div>
      ),
    },
    {
      title: 'Metadata',
      dataIndex: 'metadata',
      key: 'metadata',
      render: (metadata: Record<string, string>) => {
        const jsonString = JSON.stringify(metadata || {});
        
        return (
          <Tooltip title={<pre style={{ margin: 0 }}>{JSON.stringify(metadata || {}, null, 2)}</pre>}>
            <span 
              style={{ 
                fontFamily: 'monospace', 
                fontSize: '11px',
                cursor: 'pointer',
                color: '#722ed1'
              }}
              onClick={() => handleCopyMd5(jsonString)}
            >
              {jsonString}
            </span>
          </Tooltip>
        );
      },
      width: 200,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('vi-VN'),
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => new Date(date).toLocaleString('vi-VN'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: Version) => (
        <Space size="middle">
          <Popconfirm
            title="Bạn có chắc muốn xóa version này?"
            onConfirm={() => handleDelete(record)}
            okText="Có"
            cancelText="Không"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (versionsLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '300px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <Spin size="large" />
        <span style={{ color: '#666', fontSize: '16px' }}>Đang tải dữ liệu versions...</span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>Quản lý Versions</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Thêm Version
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={versions}
        loading={versionsLoading}
        rowKey={(record) => `${record.app_id}-${record.version}`}
        pagination={{
          total: versionsResponse?.total || 0,
          pageSize: versionsResponse?.page_size || 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} của ${total} versions`,
        }}
      />

      <Modal
        title="Thêm Version mới"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={isUploading || createMutation.isPending}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="app_id"
            label="App"
            rules={[{ required: true, message: 'Vui lòng chọn app!' }]}
          >
            <Select
              placeholder="Chọn app"
              disabled={isUploading}
              onChange={(value) => {
                const app = appsResponse?.find((a: App) => a.id === value);
                setSelectedApp(app || null);
              }}
            >
              {appsResponse?.map((app: App) => (
                <Option key={app.id} value={app.id}>
                  {app.name} ({app.distribution_type === 'Store' ? 'Store' : 'Binary'})
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedApp?.distribution_type !== 'Store' && (
            <Form.Item
              label="File Upload"
              required
            >
              <Upload
                beforeUpload={(file) => {
                  setSelectedFile(file);
                  return false; // Prevent auto upload
                }}
                onRemove={() => setSelectedFile(null)}
                maxCount={1}
                disabled={isUploading}
              >
                <Button icon={<UploadOutlined />} disabled={isUploading}>
                  Chọn file (APK, IPA, v.v.)
                </Button>
              </Upload>
              {selectedFile && (
                <div style={{ marginTop: '8px', color: '#666' }}>
                  File đã chọn: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}
            </Form.Item>
          )}

          {selectedApp?.distribution_type === 'Store' && (
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
              <strong>📱 App từ Store:</strong> Không cần upload file. Version này sẽ được tải từ App Store hoặc Google Play Store.
            </div>
          )}

          {isUploading && uploadProgress > 0 && (
            <Form.Item>
              <Progress percent={uploadProgress} status="active" />
              <span style={{ color: '#666', fontSize: '12px' }}>
                Đang upload file lên S3...
              </span>
            </Form.Item>
          )}

          <Form.Item
            name="version"
            label="Version"
            rules={[
              { required: true, message: 'Vui lòng nhập version!' },
              {
                validator: (_, value) => {
                  if (versionDuplicateStatus.isDuplicate) {
                    return Promise.reject(new Error('Version đã tồn tại!'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            validateStatus={
              versionDuplicateStatus.isChecking 
                ? 'validating' 
                : versionDuplicateStatus.isDuplicate 
                  ? 'error' 
                  : versionDuplicateStatus.isDuplicate === false 
                    ? 'success' 
                    : undefined
            }
            help={
              versionDuplicateStatus.isChecking 
                ? 'Đang kiểm tra...' 
                : versionDuplicateStatus.message
            }
          >
            <Input 
              placeholder="1.0.4" 
              disabled={isUploading}
              onChange={(e) => {
                const value = e.target.value;
                debouncedCheckVersion(value);
              }}
              suffix={
                versionDuplicateStatus.isChecking ? (
                  <Spin size="small" />
                ) : versionDuplicateStatus.isDuplicate === false ? (
                  <span style={{ color: '#52c41a' }}>✓</span>
                ) : versionDuplicateStatus.isDuplicate === true ? (
                  <span style={{ color: '#ff4d4f' }}>✗</span>
                ) : null
              }
            />
          </Form.Item>

          {selectedApp?.distribution_type !== 'Store' && (
            <Form.Item
              name="file_md5"
              label="File MD5"
              rules={[{ required: true, message: 'Vui lòng nhập MD5 của file!' }]}
            >
              <Input
                placeholder="7f7ef5fc4694726a6cc3c55396741a10"
                disabled={isUploading}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          )}

          <Form.Item
            name="release_notes"
            label="Release Notes"
          >
            <TextArea
              placeholder="Mô tả các thay đổi trong version này"
              rows={4}
              disabled={isUploading}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Versions;
